/* eslint-disable @next/next/no-img-element */
import React, { useState } from 'react';
import styles from './NativeVideo.module.scss';
import { Cross, Play } from '../../_shared/Icons';
import { Modal as LegacyEDSModal } from '@eventbrite/legacy-eds.modal';
import { track } from '@eventbrite/legacy-infra.datalayer-library';
import { LISTING_HEAP_IDS } from '../../_shared/constants';
import { DatadogVideoEvents } from '../../../tracking/loggers/DatadogRUMEvents';
import { addCustomRUMEvent } from '../../../tracking/loggers/InteractionsDatadogLogger';
import {
  getPlaybackTime,
  getStalledRatioOverDuration,
  getStalledRatioOverPlaybackTime,
  getTotalStalledTime,
} from './tracking/videoMetrics';
import type { VideoMetaData, VideoMetrics } from '../../_shared/Video/types';
import { Video } from '../../_shared/Video/Video';

interface NativeVideoProps {
  url: string;
  thumbnail?: string | null;
  children?: React.ReactNode;
}

export const NativeVideo: React.FC<NativeVideoProps> = ({ url, thumbnail, children }) => {
  const [shouldShowVideo, setShouldShowVideo] = useState(false);

  const handleLoadedMetadata = (duration: number) => {
    addCustomRUMEvent(DatadogVideoEvents.PlaybackStart, {
      location: 'fullscreen',
      value: 1,
    });

    if (!duration) {
      return;
    }
    track({
      eventName: LISTING_HEAP_IDS.VIDEO_DURATION,
      eventData: {
        eventBucketLabel: `Video duration: ${duration} seconds`,
      },
    });
  };

  const handleTimeUpdate = (currentTime: number, duration: number) => {
    if (!duration) {
      return;
    }
    const percentageOfVideoPlayed = Math.floor((currentTime / duration) * 100);
    if ([25, 50, 75, 100].includes(percentageOfVideoPlayed)) {
      track({
        eventName: LISTING_HEAP_IDS.VIDEO_PLAYED,
        eventData: {
          percentageOfVideoPlayed,
        },
      });
    }
  };

  const handleError = (error?: MediaError) => {
    addCustomRUMEvent(DatadogVideoEvents.PlaybackFailed, {
      error,
      location: 'fullscreen',
      value: 1,
    });
  };

  const handleEnded = (metrics: VideoMetrics, metadata: VideoMetaData) => {
    const playbackTime = getPlaybackTime(metrics);
    const stalledTime = getTotalStalledTime(metrics);
    const stalledRatioOverPlaybackTime = getStalledRatioOverPlaybackTime(metrics);
    const stalledRatioOverDuration = getStalledRatioOverDuration(metrics, metadata);
    addCustomRUMEvent(DatadogVideoEvents.PlaybackEnd, {
      playback_time: playbackTime,
      stalled_time: stalledTime,
      stalled_over_playback_time: stalledRatioOverPlaybackTime,
      stalled_over_video_duration: stalledRatioOverDuration,
      location: 'fullscreen',
      value: 1,
    });
  };

  return (
    <>
      <div className={styles.root}>
        <div className={styles.overlay}>
          <button
            className={styles.playButton}
            aria-label={'Play'}
            onClick={() => setShouldShowVideo(true)}
            data-heap-id={LISTING_HEAP_IDS.HERO_VIDEO}
          >
            <div className={styles.playButtonIcon}>
              <Play />
            </div>
          </button>
        </div>
        {children}
      </div>
      {shouldShowVideo && (
        <LegacyEDSModal
          isShown={true}
          onClose={() => setShouldShowVideo(false)}
          noMinHeight={false}
          type="simple"
          noPadding={true}
          closeButtonCustomIcon={(() => <Cross />) as unknown as React.ReactNode}
          closeButtonClassName={styles.closeButton}
          isClosable={true}
          isPortal={true}
          modalHostNodeSelector="body"
          dangerouslySetContainerClassName={{ __className: styles.modalContainer as string }}
          __containerClassName={styles.modal}
        >
          <div
            onClick={() => setShouldShowVideo(false)}
            className={styles.modalVideoWrapper}
            data-testid="native-video-wrapper"
          >
            <Video
              url={url}
              thumbnail={thumbnail}
              onLoadedMetadata={handleLoadedMetadata}
              onTimeUpdate={handleTimeUpdate}
              onError={handleError}
              onEnded={handleEnded}
            />
          </div>
        </LegacyEDSModal>
      )}
    </>
  );
};
